'use client';

import Link from 'next/link';
import { Layout } from '@/components/layout';
import { TokenSavedCounter, LeaderBoard } from '@/components/stats';
import { PatternGrid } from '@/components/patterns/PatternGrid';
import { Button } from '@/components/ui/button';
import { Zap, TrendingUp, Package, ArrowRight, Users } from 'lucide-react';
import { usePatterns } from '@/hooks/usePatterns';

export default function Home() {
  const { patterns, isLoading } = usePatterns({ limit: 6 });

  return (
    <Layout>
      <div className="bg-gray-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800 border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                VibeAtlas
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 mb-8">
                Discover & Share AI Patterns
              </p>
              <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
                The npm for AI prompts and patterns. Build better AI applications with
                battle-tested patterns from the community.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
                <Link href="/browse">
                  <Button size="lg" variant="primary">
                    Browse Patterns
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link href="/docs">
                  <Button variant="secondary" size="lg">
                    Documentation
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full border border-gray-700">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-medium text-gray-300">Quick Install</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full border border-gray-700">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-gray-300">Version Control</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full border border-gray-700">
                  <Package className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-gray-300">Community Driven</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full border border-gray-700">
                  <Users className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-gray-300">Collaborative</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="stats-heading">
          <h2 id="stats-heading" className="sr-only">Platform Statistics</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <TokenSavedCounter />
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 flex flex-col items-center justify-center">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                Community Impact
              </div>
              <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-2">
                Growing
              </div>
              <div className="text-sm text-gray-500">
                Join the AI patterns community
              </div>
            </div>
          </div>
        </section>

        {/* Featured Patterns */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="featured-heading">
          <div className="flex justify-between items-center mb-8">
            <h2 id="featured-heading" className="text-3xl font-bold text-gray-100">Featured Patterns</h2>
            <Link href="/browse">
              <Button variant="default">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <PatternGrid patterns={patterns} limit={6} isLoading={isLoading} />
        </section>

        {/* Leaderboard */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <LeaderBoard />
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-y border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to get started?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Install the CLI and start using patterns in your projects today.
              </p>
              <div className="bg-gray-900 rounded-lg p-6 mb-8 border border-gray-700">
                <code className="text-green-400 font-mono text-xs sm:text-sm">
                  npm install -g @vibeatlas/cli
                </code>
                <p className="text-gray-500 text-xs mt-2">Coming soon to npm registry</p>
              </div>
              <Link href="/docs/cli">
                <Button size="lg" variant="primary">
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
